import React, { useState, useCallback, useEffect } from 'react';
import { Box, Typography, Alert, CircularProgress } from '@mui/material';

import { ConditionClause, VisualConditionState } from '../../types';
import { convertVisualToText } from '../../utils/expressionConverterBackend';
import { useParseTextToVisualQuery } from '../../hooks/api/useValidationQuery';
import { useConditionElementsQuery } from '../../hooks/api/useConfigurationQuery';
import TableConditionEditor from './TableConditionEditor';
import TextConditionEditor from './TextConditionEditor';

interface VisualConditionEditorProps {
  value: string;
  onChange: (expression: string) => void;
  error?: string;
  disabled?: boolean;
  placeholder?: string;
}

const VisualConditionEditor: React.FC<VisualConditionEditorProps> = ({
  value,
  onChange,
  error,
  disabled = false,
  placeholder: _placeholder = 'No targeting conditions defined',
}) => {
  const {
    data: config,
    isLoading: configLoading,
    error: configError,
  } = useConditionElementsQuery();

  // Use ReactQuery for parsing expressions with caching
  const {
    data: parsedClauses,
    isLoading: parsingLoading,
    error: parsingError,
  } = useParseTextToVisualQuery(value ?? '', config, !configLoading);

  const [state, setState] = useState<VisualConditionState>(() => ({
    clauses: [],
    isTextMode: false,
    textExpression: value ?? '',
    isValid: true,
    validationErrors: [],
  }));

  const [showExpressionPreview, setShowExpressionPreview] = useState(false);

  useEffect(() => {
    if (parsingLoading || !parsedClauses) return;

    try {
      let newValidationErrors: string[] = [];

      if (error) {
        newValidationErrors.push(error);
      }

      if (parsingError) {
        newValidationErrors.push('Failed to parse expression');
      }

      if (!state.isTextMode && parsedClauses) {
        // Only show validation errors for truly malformed clauses (those with explicit validationError)
        // Don't show errors for incomplete clauses that are still being edited
        const clauseErrors = parsedClauses
          .filter((c: ConditionClause) => !c.isValid && c.validationError)
          .map((c: ConditionClause) => c.validationError ?? 'Unknown validation error');
        newValidationErrors = [...newValidationErrors, ...clauseErrors];
      }

      const allClausesValid = parsedClauses?.every((c: ConditionClause) => c.isValid) ?? false;
      const isValidExpression =
        !error && !parsingError && (state.isTextMode ? state.isValid : allClausesValid);

      setState(prev => ({
        ...prev,
        textExpression: value ?? '',
        clauses: prev.isTextMode ? prev.clauses : (parsedClauses ?? []),
        isValid: isValidExpression,
        validationErrors: newValidationErrors,
      }));
    } catch (err) {
      console.error('Error processing parsed expression:', err);
      // Fallback to showing error state
      setState(prev => ({
        ...prev,
        textExpression: value ?? '',
        isValid: false,
        validationErrors: [error ?? 'Failed to process expression'],
      }));
    }
  }, [value, error, state.isTextMode, state.isValid, parsedClauses, parsingLoading, parsingError]);

  const handleAddClause = useCallback(() => {
    const newClause: ConditionClause = {
      id: `clause-${Date.now()}`,
      element: '',
      operator: '==',
      value: '',
      isValid: false,
    };
    setState(prev => ({
      ...prev,
      clauses: [...prev.clauses, newClause],
    }));
  }, []);

  const handleUpdateClause = useCallback(
    (clauseId: string, updates: Partial<ConditionClause>) => {
      setState(prev => {
        const updatedClauses = prev.clauses.map(clause =>
          clause.id === clauseId ? { ...clause, ...updates } : clause
        );

        const expression = convertVisualToText(updatedClauses, config);

        // Only clear validation errors if they were related to malformed parsing, not incomplete fields
        const hasOnlyIncompleteErrors = prev.validationErrors.every(
          err => err === 'Value is required' || err === 'Incomplete clause'
        );

        const newState = {
          ...prev,
          clauses: updatedClauses,
          textExpression: expression,
          isValid: true, // Assume valid from a structural perspective; backend will confirm
          validationErrors: hasOnlyIncompleteErrors ? [] : prev.validationErrors, // Clear only incomplete errors
        };
        return newState;
      });
    },
    [config]
  );

  const handleApplyClause = useCallback(
    (_clauseId: string) => {
      // This function is called when the Apply button is clicked      // The actual validation and state update has already been done by the ConditionClauseEditor
      // We just need to trigger the expression regeneration
      setState(prev => {
        const expression = convertVisualToText(prev.clauses, config);
        return {
          ...prev,
          textExpression: expression,
          isValid: true,
          validationErrors: [],
        };
      });
    },
    [config]
  );
  const handleRemoveClause = useCallback(
    (clauseId: string) => {
      setState(prev => {
        const updatedClauses = prev.clauses.filter(clause => clause.id !== clauseId);
        const expression = convertVisualToText(updatedClauses, config);
        // Clear frontend validation errors on removal as well
        return {
          ...prev,
          clauses: updatedClauses,
          textExpression: expression,
          isValid: true, // Assume valid; backend will confirm
          validationErrors: [], // Clear frontend validation errors
        };
      });
    },
    [config]
  );

  const handleTextChange = useCallback((newText: string) => {
    // Basic check for empty text, but detailed validation is deferred to backend
    const isEmpty = newText.trim().length === 0;
    setState(prev => ({
      ...prev,
      textExpression: newText,
      isValid: !isEmpty, // Only mark as invalid if completely empty
      validationErrors: isEmpty ? ['Expression cannot be empty'] : [],
    }));
    // The onChange will be handled by the useEffect
  }, []);

  // Effect to call onChange when the text expression changes
  useEffect(() => {
    if (state.textExpression !== value) {
      onChange(state.textExpression);
    }
  }, [state.textExpression, value, onChange]);

  if (configLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress size={24} />
        <Typography variant="body2" sx={{ ml: 2 }}>
          Loading configuration...
        </Typography>
      </Box>
    );
  }

  // Config error is handled by a general alert, but we also ensure TextConditionEditor gets a specific message if needed.
  const textEditorHelperText =
    error ?? (state.validationErrors.length > 0 ? state.validationErrors.join('; ') : undefined);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" component="h3">
          Targeting Conditions
        </Typography>
      </Box>

      {configError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Configuration could not be loaded:{' '}
          {typeof configError === 'string' ? configError : 'Please try again later.'}
        </Alert>
      )}

      {/* Display external error if not already covered by configError and not in text mode with its own errors */}
      {error && !configError && !(state.isTextMode && state.validationErrors.length > 0) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Display clause-specific validation errors in visual mode */}
      {!state.isTextMode && state.validationErrors.length > 0 && !error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2" component="div">
            Validation issues:
            <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
              {state.validationErrors.map(err => (
                <li key={err}>{err}</li>
              ))}
            </ul>
          </Typography>
        </Alert>
      )}

      {state.isTextMode ? (
        <TextConditionEditor
          expression={state.textExpression}
          onChange={handleTextChange}
          disabled={disabled}
          // placeholder={placeholder} // Removed placeholder prop
          error={!state.isValid || !!error}
          helperText={textEditorHelperText}
        />
      ) : (
        <TableConditionEditor
          conditions={state.clauses}
          onUpdateCondition={handleUpdateClause}
          onApplyCondition={handleApplyClause}
          onRemoveCondition={handleRemoveClause}
          onAddCondition={handleAddClause}
          config={config}
          showExpressionPreview={showExpressionPreview}
          onToggleExpressionPreview={() => setShowExpressionPreview(!showExpressionPreview)}
        />
      )}
    </Box>
  );
};

export default VisualConditionEditor;
